import { Autocomplete, Box, Chip, Stack, TextField, createFilterOptions } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { useSupabaseCallback } from "../../server/supabase/hooks";
import { useTags } from "../tags/hooks/use-tags";
import { SupabaseTagsService } from "../tags/server/supabase-tags-service";
import { SupabaseDigitalAssetTagService } from "./server/digital-asset-tag-service";

import type { UUID } from "../../utils/uuid";
import type { Asset } from "./server/digital-asset-service";
import type { FC } from "react";

const filter = createFilterOptions<string>();
const NO_TAGS: string[] = [];

export const AssetTagManager: FC<{
  localTags: string[];
  setLocalTags: (tags: string[]) => void;
  asset?: Asset;
  availableTags?: string[];
}> = ({ localTags, setLocalTags, asset, availableTags = NO_TAGS }) => {
  const { data: tagsData } = useTags();
  const [value, setValue] = useState<string | null>(null);
  const [inputText, setInputText] = useState<string>("");
  const tagNameToId = useMemo(
    () => new Map<string, UUID>((tagsData ?? []).map((tag) => [tag.name, tag.id])),
    [tagsData],
  );
  const options = useMemo(
    () => (asset ? (tagsData ?? []).map((tag) => tag.name) : availableTags),
    [asset, availableTags, tagsData],
  );

  useEffect(() => {
    if (!asset) return;
    setLocalTags(asset.tags);
  }, [asset, setLocalTags]);

  const saveAssetTags = useSupabaseCallback(
    async ({ supabase }, nextTags: string[]): Promise<boolean> => {
      if (!asset) return true;
      const localTagMap = new Map(tagNameToId);
      const tagIds: UUID[] = [];

      for (const nextTag of nextTags) {
        let tagId = localTagMap.get(nextTag);
        if (!tagId) {
          // oxlint-disable-next-line no-await-in-loop -- sequential DB writes
          const { data, error } = await new SupabaseTagsService(supabase).upsert(
            { name: nextTag },
            { onConflict: "account_id,name" },
          );
          if (error || !data) {
            toast.error(error?.message ?? "Failed to create tag");
            return false;
          }
          tagId = data.id;
          localTagMap.set(data.name, data.id);
        }
        tagIds.push(tagId);
      }

      const { error } = await new SupabaseDigitalAssetTagService(supabase).setAssetTags(
        asset.id,
        tagIds,
      );
      if (error) {
        toast.error("Failed to save tags");
        return false;
      }
      return true;
    },
    [asset, tagNameToId],
  );

  const handleDelete = async (tag: string): Promise<void> => {
    const updatedTags = localTags.filter((t) => t !== tag);

    if (asset && !(await saveAssetTags(updatedTags))) {
      return;
    }

    setLocalTags(updatedTags);
  };

  const handleChange = async (newValue: string | null): Promise<void> => {
    const normalizedValue = newValue?.trim();
    if (!normalizedValue) {
      return;
    }

    if (localTags.includes(normalizedValue)) {
      setValue(null);
      setInputText("");
      return;
    }

    const newTags = [...localTags, normalizedValue];

    if (asset && !(await saveAssetTags(newTags))) {
      return;
    }

    setLocalTags(newTags);
    setValue(null);
    setInputText("");
  };

  return (
    <Box>
      <Autocomplete
        value={value}
        inputValue={inputText}
        onChange={(_event, newValue) => {
          void handleChange(newValue);
        }}
        onInputChange={(_event, newInputValue) => {
          setInputText(newInputValue);
        }}
        filterOptions={(os, params) => {
          const filtered = filter(os, params);

          const { inputValue } = params;
          const isExisting = os.some((option) => inputValue === option);
          if (inputValue !== "" && !isExisting) {
            filtered.push(inputValue);
          }

          return filtered;
        }}
        selectOnFocus
        clearOnBlur
        handleHomeEndKeys
        id="tag-manager-input"
        options={options.filter((option) => !localTags.includes(option))}
        getOptionLabel={(option) => option}
        renderOption={(props, option) => {
          const { key, ...optionProps } = props;
          return (
            <li key={key} {...optionProps}>
              <Chip label={option} color="primary" />
            </li>
          );
        }}
        fullWidth
        freeSolo
        renderInput={(params) => <TextField {...params} label="Add tags" />}
      />
      {localTags.length > 0 && (
        <Stack direction="row" useFlexGap sx={{ mt: 2, gap: 1, flexWrap: "wrap" }}>
          {localTags.map((tag, index) => (
            <Chip
              // oxlint-disable-next-line react/no-array-index-key -- Tag names are not guaranteed unique here; the items are stateless
              key={index}
              label={tag}
              color="primary"
              onDelete={() => void handleDelete(tag)}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
};
